import { Button, Input, Modal, Select, Tag } from "antd";
import { useState } from "react";
import { CiFilter } from "react-icons/ci";
import { FaPlus } from "react-icons/fa6";
import { FiCheckCircle, FiClock } from "react-icons/fi";
import { IoAlertCircleOutline } from "react-icons/io5";

import {
  type TicketsSidebarProps,
  type TicketType,
} from "../../../_lib/_store/interfaces/tickets";

const { Option } = Select;
const { TextArea } = Input;

export const TicketsSidebar = ({
  onSelect,
  selected,
  onAddTicket,
}: TicketsSidebarProps) => {
  const [tickets, setTickets] = useState<TicketType[]>([
    {
      id: 1,
      text: "Unable to generate compliance report",
      name: "Sarah Johnson",
      date: new Date().toLocaleDateString(),
      badge: "high",
      icon: <IoAlertCircleOutline />,
      color: "#dc2626",
      messages: [
        {
          sender: "Sarah Johnson",
          text: "Initial message",
          time: new Date(),
          type: "support",
        },
      ],
    },
    {
      id: 2,
      text: "Feature request: Custom form templates",
      name: "David Chen",
      date: new Date().toLocaleDateString(),
      badge: "medium",
      icon: <FiClock />,
      color: "#facc15",
      messages: [
        {
          sender: "David Chen",
          text: "Initial message",
          time: new Date(),
          type: "support",
        },
      ],
    },
    {
      id: 3,
      text: "Account activation issue",
      name: "Emily Davis",
      date: new Date().toLocaleDateString(),
      badge: "low",
      icon: <FiCheckCircle />,
      color: "#16a34a",
      messages: [
        {
          sender: "Emily Davis",
          text: "Initial message",
          time: new Date(),
          type: "support",
        },
      ],
    },
  ]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTicket, setNewTicket] = useState<{
    text: string;
    badge: "high" | "medium" | "low";
    message: string;
  }>({
    text: "",
    badge: "low",
    message: "",
  });

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleAddTicket = () => {
    if (!newTicket.text.trim()) return;

    const id = tickets.length + 1;
    const color =
      newTicket.badge === "high"
        ? "#dc2626"
        : newTicket.badge === "medium"
          ? "#facc15"
          : "#16a34a";

    const icon =
      newTicket.badge === "high" ? (
        <IoAlertCircleOutline />
      ) : newTicket.badge === "medium" ? (
        <FiClock />
      ) : (
        <FiCheckCircle />
      );

    const ticketToAdd: TicketType = {
      id,
      text: newTicket.text,
      name: "New User",
      badge: newTicket.badge as "high" | "medium" | "low",
      date: new Date().toLocaleDateString(),
      icon,
      color,
      messages: [
        {
          sender: "New User",
          text: newTicket.message || "Hello, new ticket created!",
          time: new Date(),
          type: "person",
        },
      ],
    };

    setTickets([...tickets, ticketToAdd]);
    onSelect(ticketToAdd);
    onAddTicket(ticketToAdd);
    setNewTicket({ text: "", badge: "low", message: "" });
    setIsModalOpen(false);
  };

  return (
    <aside className="tickets-sidebar">
      {/* Header */}
      <div className="sidebar-header">
        <h2>Support Tickets</h2>
        <Button
          type="primary"
          size="middle"
          icon={<FaPlus size={12} />}
          onClick={handleOpenModal}
        >
          New
        </Button>
      </div>

      {/* Search + Filter */}
      <div className="search-bar">
        <Input placeholder="Search tickets..." allowClear />
        <Button className="filter-btn" icon={<CiFilter size={20} />} />
      </div>

      {/* Ticket list */}
      <ul className="ticket-list">
        {tickets.map((ticket) => (
          <li
            key={ticket.id}
            className={`ticket-card ${ticket.badge} ${selected?.id === ticket.id ? "active" : ""}`}
          >
            <button
              className="ticket-card-btn"
              onClick={() => onSelect(ticket)}
              style={{ all: "unset", cursor: "pointer", width: "100%" }}
            >
              <div className="card-header">
                <h4>{ticket.text}</h4>
                <span className="card-icon" style={{ color: ticket.color }}>
                  {ticket.icon}
                </span>
              </div>
              <p className="card-name">{ticket.name}</p>
              <div className="card-footer">
                <Tag
                  className="badge"
                  color={
                    ticket.badge === "high"
                      ? "error"
                      : ticket.badge === "medium"
                        ? "gold"
                        : "green"
                  }
                >
                  {ticket.badge}
                </Tag>
                <span className="date">{ticket.date}</span>
              </div>
            </button>
          </li>
        ))}
      </ul>

      {/* Modal for new ticket */}
      <Modal
        title="New Ticket"
        open={isModalOpen}
        onCancel={handleCloseModal}
        onOk={handleAddTicket}
        okText="Add Ticket"
      >
        <Input
          placeholder="Ticket Title"
          value={newTicket.text}
          onChange={(e) => setNewTicket({ ...newTicket, text: e.target.value })}
          style={{ marginBottom: 10, width: "100%" }}
        />

        <Select
          value={newTicket.badge}
          onChange={(value) => setNewTicket({ ...newTicket, badge: value })}
          style={{ width: "100%", marginBottom: 10 }}
        >
          <Option value="high">High</Option>
          <Option value="medium">Medium</Option>
          <Option value="low">Low</Option>
        </Select>

        <TextArea
          placeholder="Initial Message (optional)"
          value={newTicket.message}
          onChange={(e) =>
            setNewTicket({ ...newTicket, message: e.target.value })
          }
          rows={3}
        />
      </Modal>
    </aside>
  );
};
